import { useEffect, useRef } from 'react';
import * as THREE from 'three';

export default function WebGL3DBackground() {
  const mountRef = useRef<HTMLDivElement>(null);
  const sceneRef = useRef<THREE.Scene | null>(null);
  const rendererRef = useRef<THREE.WebGLRenderer | null>(null);
  const animationIdRef = useRef<number | null>(null);

  useEffect(() => {
    if (!mountRef.current) return;

    // Scene setup
    const scene = new THREE.Scene();
    sceneRef.current = scene;

    // Camera setup
    const camera = new THREE.PerspectiveCamera(
      75,
      window.innerWidth / window.innerHeight,
      0.1,
      1000
    );
    camera.position.z = 30;

    // Renderer setup
    const renderer = new THREE.WebGLRenderer({ 
      alpha: true, 
      antialias: true,
      powerPreference: "high-performance"
    });
    renderer.setSize(window.innerWidth, window.innerHeight);
    renderer.setPixelRatio(Math.min(window.devicePixelRatio, 2));
    renderer.setClearColor(0x000000, 0);
    rendererRef.current = renderer;

    mountRef.current.appendChild(renderer.domElement);

    const materials = [
      new THREE.MeshPhongMaterial({ 
        color: 0x3b82f6, 
        transparent: true, 
        opacity: 0.4,
        shininess: 100
      }),
      new THREE.MeshPhongMaterial({ 
        color: 0x06b6d4, 
        transparent: true, 
        opacity: 0.3,
        shininess: 100
      }),
      new THREE.MeshPhongMaterial({ 
        color: 0x8b5cf6, 
        transparent: true, 
        opacity: 0.3,
        shininess: 100
      }),
      new THREE.MeshPhongMaterial({ 
        color: 0x10b981, 
        transparent: true, 
        opacity: 0.2,
        shininess: 100
      }),
    ];

    // Create simple geometric shapes
    const meshes: THREE.Mesh[] = [];
    const meshData: Array<{
      mesh: THREE.Mesh;
      rotationSpeed: THREE.Vector3;
      floatSpeed: number;
      initialY: number;
    }> = [];
    
    // Create simple geometric objects
    const geometries = [
      new THREE.BoxGeometry(2, 2, 2),
      new THREE.SphereGeometry(1.5, 8, 6),
      new THREE.ConeGeometry(1, 2, 8),
      new THREE.OctahedronGeometry(1.5),
      new THREE.TetrahedronGeometry(1.5),
    ];

    for (let i = 0; i < 12; i++) {
      const geometry = geometries[Math.floor(Math.random() * geometries.length)];
      const material = materials[Math.floor(Math.random() * materials.length)];
      const mesh = new THREE.Mesh(geometry, material);

      // Random positioning
      mesh.position.x = (Math.random() - 0.5) * 60;
      mesh.position.y = (Math.random() - 0.5) * 40;
      mesh.position.z = (Math.random() - 0.5) * 50;

      // Random scale
      const scale = 0.5 + Math.random() * 1.0;
      mesh.scale.set(scale, scale, scale);

      scene.add(mesh);
      meshes.push(mesh);

      // Store animation data
      meshData.push({
        mesh,
        rotationSpeed: new THREE.Vector3(
          (Math.random() - 0.5) * 0.01,
          (Math.random() - 0.5) * 0.01,
          (Math.random() - 0.5) * 0.008
        ),
        floatSpeed: 0.001 + Math.random() * 0.002,
        initialY: mesh.position.y,
      });
    }

    // Add ambient light
    const ambientLight = new THREE.AmbientLight(0x404040, 0.6);
    scene.add(ambientLight);

    // Add directional light
    const directionalLight = new THREE.DirectionalLight(0xffffff, 1);
    directionalLight.position.set(10, 10, 5);
    scene.add(directionalLight);

    // Add point lights for dynamic lighting
    const pointLight1 = new THREE.PointLight(0x3b82f6, 1, 100);
    pointLight1.position.set(20, 20, 20);
    scene.add(pointLight1);

    const pointLight2 = new THREE.PointLight(0x06b6d4, 1, 100);
    pointLight2.position.set(-20, -20, 20);
    scene.add(pointLight2);

    // Mouse interaction
    const mouse = new THREE.Vector2();
    const targetRotation = new THREE.Vector2();
    const currentRotation = new THREE.Vector2();

    const handleMouseMove = (event: MouseEvent) => {
      mouse.x = (event.clientX / window.innerWidth) * 2 - 1;
      mouse.y = -(event.clientY / window.innerHeight) * 2 + 1;
      
      targetRotation.x = mouse.y * 0.1;
      targetRotation.y = mouse.x * 0.1;
    };

    window.addEventListener('mousemove', handleMouseMove);

    // Animation loop
    let time = 0;
    const animate = () => {
      time += 0.01;

      // Smooth camera rotation based on mouse
      currentRotation.x += (targetRotation.x - currentRotation.x) * 0.05;
      currentRotation.y += (targetRotation.y - currentRotation.y) * 0.05;
      
      camera.position.x = Math.sin(currentRotation.y) * 30;
      camera.position.y = currentRotation.x * 15;
      camera.lookAt(0, 0, 0);

      // Animate meshes
      meshData.forEach((data, index) => {
        const { mesh, rotationSpeed, floatSpeed, initialY } = data;
        
        // Rotation
        mesh.rotation.x += rotationSpeed.x;
        mesh.rotation.y += rotationSpeed.y;
        mesh.rotation.z += rotationSpeed.z;

        // Floating motion
        mesh.position.y = initialY + Math.sin(time + index) * 3;
        
        // Gentle drift
        mesh.position.x += Math.sin(time * 0.5 + index) * 0.01;
        mesh.position.z += Math.cos(time * 0.3 + index) * 0.01;

        // Smooth pulsing scale effect - use consistent base scale per mesh
        if (!mesh.userData.baseScale) {
          mesh.userData.baseScale = 0.5 + Math.random() * 1.0;
        }
        const pulseScale = mesh.userData.baseScale + Math.sin(time * 1.5 + index) * 0.1;
        mesh.scale.setScalar(pulseScale);
      });

      // Animate lights
      pointLight1.position.x = Math.sin(time * 0.7) * 30;
      pointLight1.position.z = Math.cos(time * 0.7) * 30;
      
      pointLight2.position.x = Math.cos(time * 0.5) * 25;
      pointLight2.position.z = Math.sin(time * 0.5) * 25;

      renderer.render(scene, camera);
      animationIdRef.current = requestAnimationFrame(animate);
    };

    animate();

    // Handle window resize
    const handleResize = () => {
      if (!camera || !renderer) return;
      
      camera.aspect = window.innerWidth / window.innerHeight;
      camera.updateProjectionMatrix();
      renderer.setSize(window.innerWidth, window.innerHeight);
    };

    window.addEventListener('resize', handleResize);

    // Cleanup
    return () => {
      window.removeEventListener('mousemove', handleMouseMove);
      window.removeEventListener('resize', handleResize);
      
      if (animationIdRef.current) {
        cancelAnimationFrame(animationIdRef.current);
      }
      
      if (mountRef.current && renderer.domElement) {
        mountRef.current.removeChild(renderer.domElement);
      }
      
      // Dispose of Three.js objects
      meshes.forEach(mesh => {
        if (mesh.geometry) mesh.geometry.dispose();
        if (mesh.material) {
          if (Array.isArray(mesh.material)) {
            mesh.material.forEach(material => material.dispose());
          } else {
            mesh.material.dispose();
          }
        }
      });
      
      if (renderer) {
        renderer.dispose();
      }
    };
  }, []);

  return (
    <div 
      ref={mountRef} 
      className="fixed inset-0 pointer-events-none z-0"
      style={{ 
        background: 'transparent',
        overflow: 'hidden'
      }}
    />
  );
}